import { Link } from "react-router-dom";
import Header from "@/components/app/Header";
import { Button } from "@/components/ui/button";
import { ArrowRight, Camera, TrendingUp, ShieldCheck, Sparkles, Zap, CheckCircle2 } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1637228393246-c38a4b3d2011?auto=format&fit=crop&w=2000&q=80";
const SNEAKER_IMG = "https://images.unsplash.com/photo-1556906781-9a412961c28c?auto=format&fit=crop&w=1600&q=80";
const SCAN_IMG = "https://images.unsplash.com/photo-1581442616850-7bcff1d80af7?auto=format&fit=crop&w=1600&q=80";

const STEPS = [
  { n: "01", title: "Snap a photo", body: "Point your phone at any thrift find. Any brand, any condition — even before you buy." },
  { n: "02", title: "AI analyzes", body: "Our vision model identifies the item and pulls resale intel from the secondhand market." },
  { n: "03", title: "You profit", body: "Get a clear Profit Report with the best marketplace, what to offer, and risks to check." },
];

const BRANDS = ["Supreme", "Stüssy", "Nike", "Carhartt", "Palace", "Patagonia", "Levi's", "Polo", "Adidas", "Stone Island"];

export default function Landing() {
  return (
    <div className="min-h-screen mesh-bg">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0f0d]/70 via-[#0a0f0d]/85 to-[#0a0f0d]" />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-32">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 border border-emerald-500/25 bg-emerald-500/10 px-3 py-1.5 mb-8 rounded-full text-xs font-medium text-emerald-300">
              <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
              1 free scan on signup — no credit card
            </div>
            <h1 className="font-display font-semibold text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02]">
              Know exactly what<br />
              <span className="text-emerald-400">that thrift find</span> is worth.
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-zinc-300 max-w-2xl leading-relaxed">
              Snap a photo of any clothing item. ClosetFlip's AI tells you what it's worth,
              what to offer, where to sell it, and which risks to check — in seconds.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/register">
                <Button
                  size="lg"
                  data-testid="hero-cta-start"
                  className="rounded-xl bg-emerald-500 hover:bg-emerald-600 text-[#0a0f0d] font-semibold px-7 h-13 text-base shadow-xl shadow-emerald-500/20"
                >
                  Start scanning free <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button
                  size="lg"
                  variant="outline"
                  data-testid="hero-cta-pricing"
                  className="rounded-xl border-white/15 bg-white/[0.02] hover:bg-white/[0.05] font-medium px-7 h-13 text-base"
                >
                  See pricing
                </Button>
              </Link>
            </div>

            <div className="mt-14 flex flex-wrap gap-x-8 gap-y-3 text-sm text-zinc-400">
              {["Instant AI analysis", "4 major marketplaces", "No subscription"].map((f) => (
                <span key={f} className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" /> {f}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Brand marquee */}
        <div className="relative border-y border-white/5 bg-[#0f1512]/60 py-6 overflow-hidden">
          <div className="flex marquee-track whitespace-nowrap font-display text-2xl text-zinc-600">
            {[...BRANDS, ...BRANDS, ...BRANDS].map((b, i) => (
              <span key={i} className="mx-8 flex items-center gap-8">
                {b} <span className="text-emerald-500/50">·</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-6 py-28">
        <div className="max-w-2xl mb-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-emerald-400 mb-3">How it works</p>
          <h2 className="font-display font-semibold text-4xl sm:text-5xl tracking-tight">
            Three steps. No spreadsheets. No guesswork.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {STEPS.map((s) => (
            <div key={s.n} className="lift rounded-2xl border border-white/8 bg-white/[0.015] p-8">
              <div className="w-11 h-11 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center font-mono text-sm text-emerald-400 mb-6">
                {s.n}
              </div>
              <h3 className="font-display font-semibold text-2xl tracking-tight mb-3">{s.title}</h3>
              <p className="text-zinc-400 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-2xl mb-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-emerald-400 mb-3">Profit Report</p>
          <h2 className="font-display font-semibold text-4xl sm:text-5xl tracking-tight">
            Every scan. Every angle. One clear answer.
          </h2>
        </div>

        <div className="grid grid-cols-12 gap-4">
          <FeatureCard
            className="col-span-12 md:col-span-8 min-h-[340px]"
            icon={<TrendingUp className="w-5 h-5" />}
            title="Market Value Range"
            body="Instant low / high resale estimates based on brand, category, condition, and current secondhand demand across Depop, Grailed, eBay, and Vinted."
            image={SCAN_IMG}
          />
          <FeatureCard
            className="col-span-12 md:col-span-4"
            icon={<Zap className="w-5 h-5" />}
            title="Suggested Offer"
            body="Know exactly what to pay before you swipe your card."
          />
          <FeatureCard
            className="col-span-6 md:col-span-4"
            icon={<Sparkles className="w-5 h-5" />}
            title="Best Marketplace"
            body="Where this item sells fastest — and highest."
          />
          <FeatureCard
            className="col-span-6 md:col-span-4"
            icon={<ShieldCheck className="w-5 h-5" />}
            title="Risk Flags"
            body="Counterfeit signals, condition issues, demand cliffs."
          />
          <FeatureCard
            className="col-span-12 md:col-span-4"
            icon={<Camera className="w-5 h-5" />}
            title="Clear Verdict"
            body="A confident BUY / MAYBE / PASS call with a confidence score."
            image={SNEAKER_IMG}
          />
        </div>
      </section>

      {/* PRICING PREVIEW */}
      <section className="border-t border-white/5 bg-[#0f1512]/40">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-emerald-400 mb-3">Pricing</p>
            <h2 className="font-display font-semibold text-4xl sm:text-5xl tracking-tight">
              Start free. Scale when you flip.
            </h2>
            <p className="text-zinc-400 mt-4">Pay-as-you-go scan packs. No subscription. Scans never expire.</p>
          </div>
          <div className="grid md:grid-cols-4 gap-4">
            <PriceCard scans="1" price="$0" note="Free — on signup" cta="Start free" to="/register" />
            <PriceCard scans="20" price="$4.99" note="Starter" cta="Buy pack" to="/pricing" />
            <PriceCard scans="120" price="$14.99" note="Best value" cta="Buy pack" to="/pricing" highlight />
            <PriceCard scans="1000" price="$49.99" note="For pros" cta="Buy pack" to="/pricing" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-6 py-28 text-center">
        <h2 className="font-display font-semibold text-4xl sm:text-6xl tracking-tight leading-[1.05]">
          Your next flip starts <span className="text-emerald-400">right now.</span>
        </h2>
        <p className="text-zinc-400 mt-5 text-lg">One free scan. No card required.</p>
        <Link to="/register">
          <Button
            size="lg"
            data-testid="footer-cta"
            className="mt-10 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-[#0a0f0d] font-semibold px-8 h-14 text-base shadow-xl shadow-emerald-500/20"
          >
            Start free scan <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </Link>
      </section>

      <footer className="border-t border-white/5 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="font-display font-semibold text-lg">
            Closet<span className="text-emerald-400">Flip</span>
          </div>
          <p className="text-xs text-zinc-500">© 2026 ClosetFlip. Snap. Scan. Flip.</p>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ className = "", icon, title, body, image }) {
  return (
    <div className={`relative overflow-hidden rounded-2xl border border-white/8 bg-white/[0.015] lift p-8 ${className}`}>
      {image && (
        <>
          <div className="absolute inset-0 bg-cover bg-center opacity-25" style={{ backgroundImage: `url(${image})` }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f0d] via-[#0a0f0d]/85 to-transparent" />
        </>
      )}
      <div className="relative">
        <div className="w-10 h-10 rounded-lg border border-emerald-500/20 bg-emerald-500/10 flex items-center justify-center text-emerald-400 mb-5">
          {icon}
        </div>
        <h3 className="font-display font-semibold text-xl tracking-tight mb-2">{title}</h3>
        <p className="text-zinc-400 leading-relaxed max-w-lg">{body}</p>
      </div>
    </div>
  );
}

function PriceCard({ scans, price, note, cta, to, highlight }) {
  return (
    <div
      className={`rounded-2xl border p-6 flex flex-col ${
        highlight
          ? "border-emerald-500/40 bg-emerald-500/[0.06] shadow-xl shadow-emerald-500/10"
          : "border-white/8 bg-white/[0.015]"
      }`}
    >
      {highlight && (
        <div className="inline-flex self-start text-[10px] uppercase tracking-[0.18em] font-mono text-emerald-400 border border-emerald-500/30 rounded-full px-2 py-0.5 mb-3">
          Popular
        </div>
      )}
      <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono">{note}</div>
      <div className="mt-4">
        <div className="font-display font-semibold text-4xl tracking-tight leading-none">{price}</div>
        <div className="text-sm text-zinc-400 mt-2">{scans} scans</div>
      </div>
      <Link to={to} className="mt-6">
        <Button
          className={`w-full rounded-lg font-medium ${
            highlight
              ? "bg-emerald-500 hover:bg-emerald-600 text-[#0a0f0d]"
              : "bg-white/5 hover:bg-white/10 text-white"
          }`}
        >
          {cta}
        </Button>
      </Link>
    </div>
  );
}
